import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ChevronDown } from "lucide-react";
import { useClerk, UserProfile } from "@clerk/react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { PageHead } from "@/components/Brand";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ProfileFields } from "@/components/ProfileFields";
import { Helplines } from "@/pages/Ready";
import { InterestPanel } from "@/components/InterestPanel";
import { KEEP_NOTE, OUTSIDE_NZ, RESTRICTED_NOTE, cancelNote } from "@/lib/content";
import { useFixCard } from "@/components/HeldPlan";
import { errMsg, formatDate, useApi } from "@/lib/api";
import { SUPPORT_EMAIL } from "@/lib/content";
import { useMe } from "@/context/MeContext";

const Card = ({ title, summary, children, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <section className="ahu-card" data-testid={testId}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`${testId}-toggle`}
        className="w-full flex items-center justify-between gap-4 p-5 sm:px-7 text-left">
        <span className="min-w-0">
          <span className="block font-display text-lg text-charcoal">{title}</span>
          <span className="block text-sm text-ink-soft truncate" data-testid={`${testId}-summary`}>{summary}</span>
        </span>
        <ChevronDown size={20} strokeWidth={1.6} className="shrink-0 text-charcoal" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .4s ease" }} />
      </button>
      <div className="grid" style={{ gridTemplateRows: open ? "1fr" : "0fr", transition: "grid-template-rows .45s ease" }}>
        <div className="overflow-hidden">
          <div className="px-5 sm:px-7 pb-6 pt-1 border-t border-rule/70">
            <div className="pt-5">{children}</div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Saved = () => (
  <span className="inline-flex items-center gap-2 text-sm text-sage-deep fade-in" data-testid="saved-indicator"><Check size={16} />Saved</span>
);

const ProfileCard = () => {
  const { me, refresh } = useMe();
  const api = useApi();
  const [form, setForm] = useState(me.profile);
  const [saved, setSaved] = useState(me.profile);
  const [busy, setBusy] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(saved);
  const change = (f) => { setForm(f); setJustSaved(false); };
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.put("/me/profile", form); await refresh(); setSaved(form); setJustSaved(true); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const p = saved || {};
  return (
    <Card title="Profile" summary={[`${p.first_name || ""} ${p.last_name || ""}`.trim(), p.area].filter(Boolean).join(" · ")} testId="settings-profile">
      <p className="text-sm text-ink-soft mb-5">Signed in as {me.email}</p>
      <form onSubmit={save}><ProfileFields form={form} setForm={change} hideAge />
        <div className="mt-6 min-h-[48px] flex items-center">
          {dirty ? <button className="btn-charcoal fade-in" disabled={busy} data-testid="settings-profile-save-btn">{busy ? "Saving" : "Save changes"}</button>
            : justSaved ? <Saved /> : null}
        </div>
      </form>
    </Card>
  );
};

const PendingMembership = ({ m, portal, api, refresh }) => {
  const failed = m.status === "payment_failed" || m.status === "past_due";
  const plan = m.interval === "year" ? "Annual" : "Monthly";
  const cancel = async () => {
    try { await api.post("/payments/cancel-pending"); await refresh(); toast.success("Your membership has been cancelled. Nothing was charged."); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="Membership" summary={failed ? "Payment didn't go through" : `${plan} membership · no payment yet`} testId="settings-membership">
      <p data-testid="membership-status">{failed ? RESTRICTED_NOTE : `${plan} membership. Your card is registered. Your first payment is processed after you've received your first Action Plan. You can cancel any time.`}</p>
      <div className="mt-5 flex flex-col sm:flex-row gap-3">
        {failed ? <FixCardButton />
          : <button className="btn-charcoal" onClick={portal} data-testid="manage-membership-btn">Change card</button>}
        {!m.subscription_id && <ConfirmDialog title="Cancel your membership?" body="Nothing has been charged, and nothing will be. We'll remove your card." confirmLabel="Cancel membership"
          onConfirm={cancel} testId="cancel-pending" calm
          trigger={<button className="btn-outline" data-testid="cancel-pending-btn">Cancel membership</button>} />}
      </div>
    </Card>
  );
};

const FixCardButton = ({ testId = "settings-update-card-btn" }) => {
  const [fix, busy] = useFixCard();
  return <button className="btn-charcoal" disabled={busy} onClick={fix} data-testid={testId}>{busy ? "Opening checkout" : "Update my card"}</button>;
};

export const DownloadPlans = ({ className, testId = "download-plans-btn" }) => {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.get("/plans/download-all", { responseType: "blob" });
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(r.data), download: "AHU-Action-Plans.zip" });
      a.click(); URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(e.response?.status === 404 ? "You don't have any plans yet" : "We couldn't download your plans. Please try again."); }
    setBusy(false);
  };
  return <button className={className} disabled={busy} onClick={go} data-testid={testId}>{busy ? "Preparing download" : "Download my plans"}</button>;
};


const MembershipCard = () => {
  const { me, refresh } = useMe();
  const api = useApi();
  const m = me.membership;
  const portal = async () => {
    try { const { data } = await api.post("/payments/portal", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (["registered", "restricted"].includes(me.access)) return <PendingMembership m={m} portal={portal} api={api} refresh={refresh} />;
  return (
    <Card title="Membership" summary={m?.complimentary ? "Honorary membership" : me.is_member ? `${m.interval === "year" ? "Annual" : "Monthly"} member · ${m.cancel_at_period_end || m.status === "canceled" ? "ends" : "renews"} ${formatDate(m.current_period_end)}` : me.access === "lapsed" ? "Membership ended" : "Free plan"} testId="settings-membership">
      {m?.complimentary ? (
        <p data-testid="membership-status">You have an honorary AHU membership, with full access to every plan feature and the Ako resources.</p>
      ) : me.is_member ? (
        <>
          <p data-testid="membership-status">{m.interval === "year" ? "Annual" : "Monthly"} membership · {m.cancel_at_period_end || m.status === "canceled" ? `Ends ${formatDate(m.current_period_end)}` : `Renews ${formatDate(m.current_period_end)}`}</p>
          <p className="text-sm text-ink-soft mt-2" data-testid="settings-keep-note">{KEEP_NOTE}</p>
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <button className="btn-charcoal" onClick={portal} data-testid="manage-membership-btn">Change card</button>
            {!m.cancel_at_period_end && m.status !== "canceled" && (
              <ConfirmDialog title="Cancel your membership?" body={cancelNote(formatDate(m.current_period_end))} confirmLabel="Continue to cancel"
                onConfirm={portal} testId="cancel-member" calm extra={<DownloadPlans className="btn-outline self-start" testId="cancel-download-plans-btn" />}
                trigger={<button className="btn-outline" data-testid="cancel-member-btn">Cancel membership</button>} />
            )}
            <DownloadPlans className="btn-quiet" testId="settings-download-plans-btn" />
          </div>
        </>
      ) : (
        <>
          <p className="text-ink-soft">{me.access === "lapsed" ? "Your membership has ended. Rejoin to get back into AHU and your Action Plans." : "You're on the free plan. Your first Action Plan is yours to keep."}</p>
          {me.profile?.area === OUTSIDE_NZ ? <div className="mt-5"><InterestPanel compact /></div>
            : <Link to="/membership" className="btn-charcoal mt-5" data-testid="settings-become-member-btn">See membership</Link>}
        </>
      )}
    </Card>
  );
};

const FeedbackCard = () => {
  const api = useApi();
  const [msg, setMsg] = useState("");
  const [sent, setSent] = useState(false);
  const send = async () => {
    try { await api.post("/feedback", { message: msg }); setMsg(""); setSent(true); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="Help us improve" summary="Tell us what's working, and what isn't" testId="settings-feedback">
      <textarea className="field" rows={4} value={msg} onChange={(e) => { setMsg(e.target.value); setSent(false); }} placeholder="What's working, and what isn't?" data-testid="feedback-input" />
      <div className="mt-4 min-h-[48px] flex items-center">
        {msg.trim().length >= 2 ? <button className="btn-charcoal fade-in" onClick={send} data-testid="feedback-send-btn">Send feedback</button>
          : sent ? <span className="inline-flex items-center gap-2 text-sm text-sage-deep fade-in" data-testid="feedback-sent"><Check size={16} />Sent. Thank you, we read every message.</span> : null}
      </div>
    </Card>
  );
};

export const SecurityPage = () => (
  <AppShell wide>
    <Link to="/settings" className="btn-quiet mb-6" data-testid="security-back-link">Back to settings</Link>
    <div className="flex justify-center"><UserProfile routing="path" path="/settings/security" /></div>
  </AppShell>
);

export default function SettingsPage() {
  const { me, refresh } = useMe();
  const api = useApi();
  const nav = useNavigate();
  const { signOut } = useClerk();

  const toggleReminders = async (v) => {
    try { await api.put("/me/reminders", { enabled: v }); await refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleWelcomeBack = async (v) => {
    try { await api.put("/me/welcome-back", { enabled: v }); await refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const deleteAll = async () => {
    try { const { data } = await api.delete("/plans"); await refresh(); toast.success(`${data.deleted} plans deleted`); } catch (e) { toast.error(errMsg(e)); }
  };
  const closeAccount = async () => {
    try { await api.delete("/account"); await signOut({ redirectUrl: "/start" }); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <AppShell>
      <PageHead title="Settings" sub="Your account, membership and support, in one place." />
      <div className="space-y-3" data-testid="settings-page">
        {me.access === "restricted" && me.role !== "admin" && (
          <div className="ahu-card p-5 border-l-4 border-gold" data-testid="restricted-banner">
            <p className="font-medium text-charcoal">{RESTRICTED_NOTE}</p>
            <div className="mt-4"><FixCardButton testId="restricted-update-card-btn" /></div>
          </div>
        )}
        {me.role === "admin" && (
          <Card title="Admin" summary="Safety alerts, users and plan access" testId="settings-admin"><Link to="/admin" className="btn-charcoal" data-testid="admin-link">Open the admin area</Link></Card>
        )}
        <ProfileCard />
        <Card title="Security" summary="Password and how you sign in" testId="settings-security">
          <p className="text-ink-soft mb-4">Change your password and manage how you sign in.</p>
          <button className="btn-outline" onClick={() => nav("/settings/security")} data-testid="security-open-btn">Change password</button>
        </Card>
        <Card title="Reminders" summary={`Check-ins ${me.reminders_enabled ? "on" : "off"} · Welcome back ${me.welcome_back_emails ? "on" : "off"}`} testId="settings-reminders">
          <div className="flex items-center justify-between gap-4">
            <p className="text-[15px]">Email me on my check-in dates{!me.is_member && me.role !== "admin" ? " (members)" : ""}</p>
            <Switch checked={me.reminders_enabled} onCheckedChange={toggleReminders} data-testid="reminders-switch" />
          </div>
          <div className="flex items-center justify-between gap-4 mt-5 pt-5 border-t border-rule">
            <p className="text-[15px]">Send me a welcome back email<span className="block text-sm text-ink-soft">When I come back after 30 days or more</span></p>
            <Switch checked={me.welcome_back_emails} onCheckedChange={toggleWelcomeBack} data-testid="welcome-back-switch" />
          </div>
        </Card>
        <MembershipCard />
        <FeedbackCard />
        <Card title="Help & Support" summary={`${SUPPORT_EMAIL} · helplines`} testId="settings-help">
          <p>Email us at <a className="underline underline-offset-4" href={`mailto:${SUPPORT_EMAIL}`} data-testid="support-email-link">{SUPPORT_EMAIL}</a>.</p>
          <p className="mt-4 text-sm text-ink-soft">AHU isn't a crisis service. If you need support now:</p>
          <Helplines />
        </Card>
        <Card title="Legal" summary="Privacy, terms and how we look after your data" testId="settings-legal">
          <div className="flex flex-col gap-2">
            <Link className="btn-quiet" to="/privacy" data-testid="privacy-link">Privacy Policy</Link>
            <Link className="btn-quiet" to="/terms" data-testid="terms-link">Terms of Use</Link>
          </div>
          <p className="mt-4 text-sm text-ink-soft">We only use essential sign-in cookies. No advertising, no tracking. Raw answers to the guided questions are deleted 6 months after each plan is created.</p>
        </Card>
        <Card title="Your data" summary="Delete plans or close your account" testId="settings-data">
          <div className="flex flex-col sm:flex-row gap-4">
            <ConfirmDialog title="Delete all your plans?" body="Every Action Plan in your account will be removed. This can't be undone." confirmLabel="Delete all plans" onConfirm={deleteAll}
              testId="delete-all" trigger={<button className="text-sm text-warn underline underline-offset-4 text-left" data-testid="delete-all-plans-btn">Delete all plans</button>} />
            <ConfirmDialog title="Close your account?" body="Your account and personal details will be deleted. Only anonymised plan content that can't be linked to you is kept." confirmLabel="Close my account" onConfirm={closeAccount}
              testId="close-account" trigger={<button className="text-sm text-warn underline underline-offset-4 text-left" data-testid="close-account-btn">Close my account</button>} />
          </div>
        </Card>
        <div className="pt-5"><button className="btn-outline w-full sm:w-auto" onClick={() => signOut({ redirectUrl: "/sign-in" })} data-testid="logout-btn">Log out</button></div>
      </div>
    </AppShell>
  );
}
